import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import type {DoppeCheckoutActionSettings} from '../../client-server-common/types/doppe-checkout-action-settings';
import {
    doppeCheckoutActionCheckoutSettingsResolveForAction,
    prepareCheckoutSettingsForViewer,
} from '../../client-server-common/types/doppe-checkout-action-settings';

import {
    arrayFlatten,
    arrayRemoveNullValues,
    asArray,
    hideableValueCreateHidden,
    hideableValueIsVisibleAndNotEqualValue,
    immerProduce
} from '@wix/devzai-utils-common';
import type {DoppeActionTypeRequest} from '../doppe-action-type-request/doppe-action-type-request';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    convertCommonListSettingToDefaultDoppeActionCtaButtonSettingsFromListAction,
    DoppeAutomationTriggers, doppeListActionContentSettingsMetadataCreateForListActionCommonSettings,
    doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails,
    doppeListActionGetDefaultListSettings,
    doppeListActionGetDefaultSearchListSettings,
    doppeListActionGetItemClickBehaviourDefaultListSettings,
    doppeListActionGetMainDetailsDefaultSettings,
    doppeListActionListItemSettingsMigrateBaseSettings,
    DoppeListActionPartialSettings,
    DoppeListActionSettings,
    doppeListActionSettingsFilterHiddenListItemsFromActionView,
    doppeListActionWithMainDetailsGetMainImage,
    doppeListActionWithMainDetailsReplaceMainImage,
    doppePremiumFeatureIsDisabled,
    DoppePremiumFeatures
} from '../../client-server-common';
import {doppeActionCtaButtonsResetSettingsForLock} from '../../client-server-common/types/doppe-action-cta-button';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import {
    doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration
} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';


export namespace DoppeActionTypeRequests {

    export interface RequestSettings extends
        DoppeListActionSettings.ListItemBaseSettings,
        DoppeActionTypeRequest.RequestOption {
    }

    export interface ActionSettings extends
        DoppeListActionSettings<DoppeActionTypeRequests.RequestSettings>,
        DoppeListActionSettings.WithSearchSettings,
        DoppeListActionSettings.WithMainDetails,
        DoppeListActionSettings.WithItemClickBehaviour,
        DoppeCheckoutActionSettings {
        itemShowPrice: boolean;
        previewShowTitle: boolean;
        previewShowDescription: boolean;
        previewCheckoutButtonText: string;
    }

    export interface DeprecatedItemFields  {
    }

    export interface DeprecatedFields extends Partial<DoppeListActionSettings.WithCtaButton> {
        requestOptions?: DoppeActionTypeRequests.RequestSettings[];
    }

    export type PartialProps = DoppeListActionPartialSettings<ActionSettings, RequestSettings, DeprecatedFields, DeprecatedItemFields>;

}


export const doppeActionTypeRequests = doppeActionTypeDefineMetadata<DoppeActionTypeRequests.ActionSettings, DoppeActionTypeRequests.PartialProps>({
    id: 'co.hopp.action/request',
    resolveActionSettings: action => {
        const {
            checkoutSettings,
            ctaButton,
            listDataItems = [],
            itemShowPrice = true,
            previewShowTitle = true,
            previewShowDescription = true,
            previewCheckoutButtonText = '',
            ...restSettings
        } = action.settings;

        return {
            ...restSettings,
            itemShowPrice: itemShowPrice,
            previewShowTitle: previewShowTitle,
            previewShowDescription: previewShowDescription,
            previewCheckoutButtonText: previewCheckoutButtonText,
            ...doppeListActionGetDefaultSearchListSettings(action.settings),
            ...doppeListActionGetDefaultListSettings<DoppeActionTypeRequests.RequestSettings>({
                ...action.settings,
                listDataItems: listDataItems.map(requestOption => {

                    const {
                        title = '',
                        description = hideableValueCreateHidden(''),
                        priceData = {actualPrice: 0, currencyCode: 'USD'} as DoppeActionTypeRequest.RequestOption['priceData'],
                        images = null,
                        fulfillmentDetails = hideableValueCreateHidden(''),
                        requestsInstructions = hideableValueCreateHidden(''),
                        ribbon = hideableValueCreateHidden(''),
                        showNotesInput = true,
                        previewCheckoutButtonText: itemPreviewCheckoutButtonText = hideableValueCreateHidden(previewCheckoutButtonText),
                        ...restRequestSettings
                    } = requestOption;

                    return {
                        ...restRequestSettings,
                        ...doppeListActionListItemSettingsMigrateBaseSettings(requestOption),
                        title: title,
                        description: description,
                        priceData: priceData,
                        images: images,
                        fulfillmentDetails: fulfillmentDetails,
                        requestsInstructions: requestsInstructions,
                        ribbon: ribbon,
                        showNotesInput: showNotesInput,
                        previewCheckoutButtonText: itemPreviewCheckoutButtonText,
                    }
                }),
            }),
            ...doppeListActionGetItemClickBehaviourDefaultListSettings(action.settings),
            ...doppeListActionGetMainDetailsDefaultSettings({}, action.settings),
            ...convertCommonListSettingToDefaultDoppeActionCtaButtonSettingsFromListAction(action.settings),
            ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                ctaButton: ctaButton,
                hasSourceURL: false
            }),
            checkoutSettings: doppeCheckoutActionCheckoutSettingsResolveForAction(checkoutSettings),
        } satisfies DoppeActionTypeRequests.ActionSettings;
    },
    prepareForViewer: (actionSettings, options) => {
        prepareCheckoutSettingsForViewer(actionSettings);
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            // doppeListActionItemCtaButtonsResetSettingsForLock(actionSettings);
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            ...doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails(),
            ...doppeListActionContentSettingsMetadataCreateForListActionCommonSettings(),
            itemShowPrice: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.itemShowPrice'}),
            previewShowTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.previewShowTitle'}),
            previewShowDescription: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.previewShowDescription'}),
            previewCheckoutButtonText: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityActionButtonText)
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                mainImage: true,
                mainTitle: true
            },
        bindableContentSettingsNames: {}
    },
    actionListItemSettingsMetadata: {
        contentSettingsMetadata: {
            title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            images: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
            fulfillmentDetails: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
            requestsInstructions: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
            previewCheckoutButtonText: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityActionButtonText),
            ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon),
            showNotesInput: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.showNotesInput'}),
        },
        supportsActionCtaButtons: false,
        supportsContentSections: false,
        hideableContentSettingsNames: {
                description: true,
                fulfillmentDetails: true,
                previewCheckoutButtonText: true,
                requestsInstructions: true,
                ribbon: true
            },
        bindableContentSettingsNames: {}
    },
    isWidgetOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    isPaymentAction: true,
    getSupportedAutomationTrigger: () => DoppeAutomationTriggers.PaymentReceived,
    isFormAction: false,
    isPremiumAction: (premiumFeaturesStatus) => {
        return doppePremiumFeatureIsDisabled(premiumFeaturesStatus, DoppePremiumFeatures.creators_monetization_actions)
    },
    isListItemLinkOpener: false,
    supportsListLayout: true,
    resolveUsedMedia: actionSettings => {
        return arrayRemoveNullValues([...arrayFlatten(actionSettings.listDataItems.map(itemSettings => {
            return itemSettings.images ?? []
        })), hideableValueIsVisibleAndNotEqualValue(actionSettings.mainImage, null) ? actionSettings.mainImage : null]);
    },
    resolveMainMedia: actionSettings => {
        return doppeListActionWithMainDetailsGetMainImage(actionSettings, actionSettings.listDataItems[0]?.images?.[0] ?? null);
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },

    resolveListItemsCount: actionSettings => {
        return doppeListActionSettingsFilterHiddenListItemsFromActionView(actionSettings.listDataItems).length;
    },
    resolveListItems: action => {
        const actionSettings = action.settings;
        return actionSettings.listDataItems.map(item => {
            return {
                title: item.title,
                action: action,
                couponCode: hideableValueCreateHidden(''),
                description: item.description,
                image: item.images ? asArray(item.images)[0] : null,
                itemId: item.id,
                ribbon: hideableValueCreateHidden(''),
                url: null,
                enabled: item.enabled,
                deleted: item.deleted,
                displayInSearch: item.displayInSearch
            }
        });
    },
    resolveListSearchSettings: actionSettings => {
        return actionSettings.listSearchSettings;
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            for (const item of actionSettings.listDataItems) {
                if (item.images) {
                    const images = asArray(item.images);
                    item.images = images.map(image => {
                        return wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap)
                    });
                }
            }

            doppeListActionWithMainDetailsReplaceMainImage(actionSettings, externalImagesUrlToImageResourceMap);
        })
    },
});
